"use client";

import type { ReactNode } from "react";

interface ScrollToLinkProps {
  /** `id` of the section to scroll to. */
  target: string;
  className?: string;
  children: ReactNode;
}

/**
 * An in-page jump that eases instead of teleporting.
 *
 * Doing it here rather than with `scroll-behavior: smooth` on `html` keeps the
 * behaviour on the two links that want it, instead of changing every anchor in
 * the app from one line in globals.css. The `href` stays real, so middle-click,
 * copy-link and a JS-less load all still work.
 */
export default function ScrollToLink({
  target,
  className,
  children,
}: ScrollToLinkProps) {
  return (
    <a
      href={`#${target}`}
      className={className}
      onClick={(event) => {
        const section = document.getElementById(target);
        if (!section) {
          return;
        }
        event.preventDefault();
        const reduced =
          typeof window.matchMedia === "function" &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        section.scrollIntoView({
          behavior: reduced ? "auto" : "smooth",
          block: "start",
        });
        // The hash is still worth setting: it is what makes the position
        // shareable and survives a reload.
        window.history.replaceState(null, "", `#${target}`);
      }}
    >
      {children}
    </a>
  );
}
